import { useContext, useEffect, useState, useMemo, type ReactNode } from 'react';
import type { InsforgeUser, OAuthProvider } from '../types';
import { NavigationProvider, BrowserNavigationAdapter } from '../navigation';
import { InsforgeManager, type InsforgeManagerState } from '../core/InsforgeManager';
import {
  InsforgeContext,
  InsforgeAuthStateContext,
  InsforgeMethodsContext,
  InsforgeConfigContext,
  type InsforgeContextValue,
  type InsforgeAuthState,
  type InsforgeAuthMethods,
  type InsforgeAuthConfig,
} from '../contexts';
import { StyleProvider } from '../styles/StyleProvider';
import { InsForgeClient } from '@insforge/sdk';

export interface InitialAuthState {
  user?: InsforgeUser | null;
  userId?: string | null;
}

export interface InsforgeProviderProps {
  /**
   * The base URL of the InsForge backend.
   * @deprecated This prop is no longer used.
   * Use the client prop instead.
   */
  baseUrl?: string;
  children: ReactNode;
  /**
   * The InsForge SDK client instance.
   */
  client: InsForgeClient;
  /**
   * URL to redirect to after successful sign in (when token is detected in URL)
   * @default '/'
   */
  afterSignInUrl?: string;
  onAuthChange?: (user: InsforgeUser | null) => void;
  onSignIn?: (authToken: string, user: InsforgeUser) => Promise<void>;
  onSignOut?: () => Promise<void>;
  onRefresh?: (authToken: string, user: InsforgeUser) => Promise<void>;
  /**
   * Initial auth state from server (for SSR hydration)
   * @internal - Not intended for public use, used by Next.js package
   */
  initialState?: InitialAuthState;
}

/**
 * Unified Insforge Provider - manages authentication state and configuration
 *
 * Uses singleton InsforgeManager to manage state across packages.
 * Context only subscribes to Manager and triggers React re-renders.
 *
 * Architecture (pattern learned from Clerk):
 * - InsforgeMethodsContext: Stable method references (NEVER change)
 * - InsforgeAuthStateContext: Reactive auth state (changes on sign in/out)
 * - InsforgeConfigContext: Static configuration values
 * - InsforgeContext: Combined context for backward compatibility
 *
 * This architecture prevents useEffect re-runs when auth state changes,
 * solving the "duplicate request" bug in components like VerifyEmail.
 *
 * @example
 * ```tsx
 * // Basic usage (React/Vite)
 * import { InsforgeProvider } from '@insforge/react';
 * import { createClient } from '@insforge/sdk';
 *
 * const client = createClient({
 *   baseUrl: import.meta.env.VITE_INSFORGE_BASE_URL,
 * });
 *
 * export default function MyApp() {
 *   return (
 *     <InsforgeProvider client={client}>
 *       <App />
 *     </InsforgeProvider>
 *   );
 * }
 * ```
 *
 * @example
 * ```tsx
 * // With cookie sync (Next.js optimization)
 * <InsforgeProvider
 *   client={client}
 *   afterSignInUrl="/dashboard"
 *   onSignIn={async (authToken) => {
 *     await signIn(authToken);
 *   }}
 *   onSignOut={async () => {
 *     await signOut();
 *   }}
 * >
 *   {children}
 * </InsforgeProvider>
 * ```
 */
export function InsforgeProviderCore({
  children,
  client,
  afterSignInUrl = '/',
  onAuthChange,
  onSignIn,
  onSignOut,
  onRefresh,
  initialState,
}: InsforgeProviderProps) {
  // Get singleton Manager instance
  const manager: InsforgeManager = useMemo(
    () =>
      InsforgeManager.getInstance({
        client,
        afterSignInUrl,
        onAuthChange,
        onSignIn,
        onSignOut,
        onRefresh,
      }),
    [client, afterSignInUrl, onAuthChange, onSignIn, onSignOut, onRefresh]
  );

  // Set initialState if provided (from Server Component)
  // This must happen during render, before useState initialization
  if (initialState) {
    const currentState = manager.getState();
    if (currentState.userId === undefined && initialState.userId !== undefined) {
      manager.setInitialState(initialState);
    }
  }

  // Subscribe to Manager state
  // Start with initial state from manager (will include initialState if set above)
  const [state, setState] = useState<InsforgeManagerState>(() => manager.getState());

  useEffect(() => {
    // Subscribe to state changes
    const unsubscribe = manager.subscribe((newState: InsforgeManagerState) => {
      setState(newState);
    });

    // Initialize auth state
    void manager.initialize();

    return () => {
      unsubscribe();
    };
  }, [manager]);

  // ============================================
  // STABLE METHOD REFERENCES
  // These NEVER change once created (only depend on manager)
  // This prevents useEffect re-runs when state changes
  // ============================================
  const stableMethods = useMemo<InsforgeAuthMethods>(
    () => ({
      setUser: (user: InsforgeUser | null) => manager.setUser(user),
      signIn: (email: string, password: string) => manager.signIn(email, password),
      signUp: (email: string, password: string, options?: { emailRedirectTo?: string }) =>
        manager.signUp(email, password, options),
      signOut: () => manager.signOut(),
      updateUser: (data: InsforgeUser['profile']) => manager.updateUser(data),
      reloadAuth: () => manager.reloadAuth(),
      resendVerificationEmail: (email: string, options?: { emailRedirectTo?: string }) =>
        manager.resendVerificationEmail(email, options),
      sendResetPasswordEmail: (email: string) => manager.sendResetPasswordEmail(email),
      resetPassword: (token: string, newPassword: string) =>
        manager.resetPassword(token, newPassword),
      verifyEmail: (otp: string, email?: string) => manager.verifyEmail(otp, email),
      exchangeResetPasswordToken: (email: string, code: string) =>
        manager.exchangeResetPasswordToken(email, code),
      loginWithOAuth: (provider: OAuthProvider, redirectTo: string) =>
        manager.loginWithOAuth(provider, redirectTo),
      getPublicAuthConfig: () => manager.getPublicAuthConfig(),
      getSession: () => manager.getSession(),
    }),
    [manager] // Only depends on manager (stable)
  );

  // ============================================
  // STABLE CONFIG REFERENCES
  // These rarely change after initialization
  // ============================================
  const stableConfig = useMemo<InsforgeAuthConfig>(
    () => ({
      baseUrl: manager.getConfig().client.getHttpClient().baseUrl,
      afterSignInUrl: manager.getConfig().afterSignInUrl || '/',
    }),
    [manager] // Only depends on manager (stable)
  );

  // ============================================
  // REACTIVE AUTH STATE
  // This updates when auth state changes (sign in/out)
  // ============================================
  const authState = useMemo<InsforgeAuthState>(
    () => ({
      user: state.user,
      userId: state.userId,
      isLoaded: state.isLoaded,
      isSignedIn: state.isSignedIn,
    }),
    [state.user, state.userId, state.isLoaded, state.isSignedIn]
  );

  // ============================================
  // COMBINED CONTEXT VALUE (for backward compatibility)
  // Uses stable method references from stableMethods
  // ============================================
  const contextValue = useMemo<InsforgeContextValue>(
    () => ({
      // Reactive state
      ...authState,
      // Stable methods (references don't change!)
      ...stableMethods,
      // Stable config
      ...stableConfig,
    }),
    [authState, stableMethods, stableConfig]
  );

  // Multi-context architecture: each context has its own responsibility
  // - MethodsContext: stable method references
  // - AuthStateContext: reactive auth state
  // - ConfigContext: static configuration
  // - InsforgeContext: combined for backward compatibility
  return (
    <InsforgeMethodsContext.Provider value={stableMethods}>
      <InsforgeConfigContext.Provider value={stableConfig}>
        <InsforgeAuthStateContext.Provider value={authState}>
          <InsforgeContext.Provider value={contextValue}>{children}</InsforgeContext.Provider>
        </InsforgeAuthStateContext.Provider>
      </InsforgeConfigContext.Provider>
    </InsforgeMethodsContext.Provider>
  );
}

export function InsforgeProvider(props: InsforgeProviderProps) {
  return (
    <StyleProvider>
      <NavigationProvider adapter={BrowserNavigationAdapter}>
        <InsforgeProviderCore {...props} />
      </NavigationProvider>
    </StyleProvider>
  );
}

/**
 * Hook to access Insforge context
 *
 * Works seamlessly across packages thanks to singleton Manager.
 * Context instance is guaranteed to be consistent.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { user, isSignedIn, signOut } = useInsforge();
 *
 *   if (!isSignedIn) return <SignIn />;
 *
 *   return (
 *     <div>
 *       <p>Welcome {user.email}</p>
 *       <button onClick={signOut}>Sign Out</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useInsforge(): InsforgeContextValue {
  const context: InsforgeContextValue | undefined = useContext(InsforgeContext);

  if (!context) {
    return {
      user: undefined,
      userId: undefined,
      isLoaded: false,
      isSignedIn: undefined,
      setUser: () => {},
      signIn: () => Promise.resolve({ error: 'SSR mode' }),
      signUp: () => Promise.resolve({ error: 'SSR mode' }),
      signOut: () => Promise.resolve(),
      updateUser: () => Promise.resolve({ error: 'SSR mode' }),
      reloadAuth: () => Promise.resolve({ success: false, error: 'SSR mode' }),
      resendVerificationEmail: () => Promise.resolve(null),
      sendResetPasswordEmail: () => Promise.resolve(null),
      resetPassword: () => Promise.resolve(null),
      verifyEmail: () => Promise.resolve({ error: 'SSR mode' }),
      exchangeResetPasswordToken: () => Promise.resolve({ error: { message: 'SSR mode' } }),
      loginWithOAuth: () => Promise.resolve(),
      getPublicAuthConfig: () => Promise.resolve(null),
      getSession: () => Promise.resolve(null),
      baseUrl: '',
      afterSignInUrl: '/',
    };
  }

  return context;
}
